import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EPISODE_TYPES, type EpisodeType } from '@podcastwiz-app/shared';
import {
  formatDuration,
  isAnalysing,
  isMastering,
  loudnessVerdict,
  useAnalyzeMedia,
  useDeleteEpisode,
  useEpisode,
  useMediaFile,
  useRefreshFeed,
  useUpdateEpisode,
  useUploadMedia,
  type ShowEpisode,
} from '../lib/shows';
import { errorInfo } from '../lib/api';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { SelectField, TextArea, TextField } from '../components/ui/Field';
import { Badge, ErrorMessage, Loading } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { AudioDrop, StatusBadge, Waveform } from '../components/shows/bits';
import { PlayerProvider, usePlayer } from '../components/episodes/Player';
import { TranscriptPanel } from '../components/episodes/TranscriptPanel';
import { AiTools } from '../components/episodes/AiTools';
import { useLatestTranscription } from '../lib/transcripts';

export function EpisodeDetailPage() {
  const { podcastId, episodeId } = useParams<{ podcastId: string; episodeId: string }>();
  const episode = useEpisode(episodeId);

  if (episode.isLoading) return <Loading />;
  if (episode.isError || !episode.data) return <ErrorMessage error={episode.error} onRetry={() => episode.refetch()} />;
  return <EpisodeView key={episode.data.id} episode={episode.data} showId={podcastId!} />;
}

function EpisodeView({ episode, showId }: { episode: ShowEpisode; showId: string }) {
  const [deleting, setDeleting] = useState(false);
  const remove = useDeleteEpisode(episode);
  const navigate = useNavigate();
  const toast = useToast();

  return (
    <PlayerProvider>
      <div className="space-y-6">
        <nav aria-label="Breadcrumb" className="text-sm text-muted">
          <Link to={`/podcasts/${showId}`} className="hover:text-accent">
            ← Back to show
          </Link>
        </nav>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">
            {episode.episodeNumber ? <span className="mr-2 text-muted">#{episode.episodeNumber}</span> : null}
            {episode.title}
          </h1>
          <StatusBadge status={episode.status} />
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <DetailsForm episode={episode} />
          <div className="space-y-6">
            <AudioCard episode={episode} />
            <PublishCard episode={episode} />
          </div>
        </div>

        <TranscriptPanel mediaFileId={episode.mediaFileId} episodeId={episode.id} />
        <EpisodeAiTools episode={episode} />

        <section className="rounded-xl border border-danger-soft p-4">
          <h2 className="text-sm font-semibold text-danger">Delete episode</h2>
          <Button variant="danger" size="sm" className="mt-3" onClick={() => setDeleting(true)}>
            Delete episode
          </Button>
        </section>
        <ConfirmDialog
          open={deleting}
          danger
          title={`Delete “${episode.title}”?`}
          body="This can’t be undone. If it was published it disappears from the feed at the next refresh."
          confirmLabel="Delete episode"
          busy={remove.isPending}
          onCancel={() => setDeleting(false)}
          onConfirm={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                toast.show('Episode deleted', 'success');
                navigate(`/podcasts/${showId}`);
              },
              onError: () => toast.show('Could not delete the episode', 'error'),
            })
          }
        />
      </div>
    </PlayerProvider>
  );
}

function EpisodeAiTools({ episode }: { episode: ShowEpisode }) {
  const latest = useLatestTranscription(episode.mediaFileId);
  if (latest.data?.status !== 'completed') return null;
  return <AiTools transcriptionId={latest.data.id} episode={episode} />;
}

/** Local form state that takes server updates while the user hasn't changed it. */
export function useServerField(value: string): [string, (v: string) => void] {
  const [local, setLocal] = useState(value);
  const synced = useRef(value);
  useEffect(() => {
    setLocal((current) => (current === synced.current ? value : current));
    synced.current = value;
  }, [value]);
  return [local, setLocal];
}

function DetailsForm({ episode }: { episode: ShowEpisode }) {
  const update = useUpdateEpisode(episode);
  const toast = useToast();
  // AI tools change the title and show notes on the server; these fields
  // follow unless edited, so saving never writes back a stale copy.
  const [title, setTitle] = useServerField(episode.title);
  const [description, setDescription] = useServerField(episode.description ?? '');
  const [showNotes, setShowNotes] = useServerField(episode.showNotes ?? '');
  const [number, setNumber] = useState(episode.episodeNumber ? String(episode.episodeNumber) : '');
  const [type, setType] = useState<EpisodeType>(episode.episodeType);
  const [titleError, setTitleError] = useState<string | undefined>();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError('Title is required.');
      return;
    }
    setTitleError(undefined);
    const n = Number.parseInt(number, 10);
    update.mutate(
      {
        title: title.trim(),
        description,
        showNotes,
        episodeType: type,
        ...(Number.isFinite(n) && n > 0 ? { episodeNumber: n } : {}),
      },
      { onSuccess: () => toast.show('Episode saved', 'success') },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-border p-5" noValidate>
      <h2 className="text-lg font-semibold">Details</h2>
      <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={titleError} maxLength={200} />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Episode number" type="number" min={1} value={number} onChange={(e) => setNumber(e.target.value)} />
        <SelectField
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value as EpisodeType)}
          options={EPISODE_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
        />
      </div>
      <TextArea label="Summary" hint="One or two sentences shown in podcast apps." value={description} onChange={(e) => setDescription(e.target.value)} />
      <TextArea
        label="Show notes"
        hint="Links, guests, timestamps. AI can draft these from the transcript."
        value={showNotes}
        onChange={(e) => setShowNotes(e.target.value)}
        className="min-h-40"
      />
      {update.error !== null && <ErrorMessage error={update.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save details
        </Button>
      </div>
    </form>
  );
}

function AudioCard({ episode }: { episode: ShowEpisode }) {
  const media = useMediaFile(episode.mediaFileId);
  const qc = useQueryClient();
  const upload = useUploadMedia();
  const update = useUpdateEpisode(episode);
  const toast = useToast();
  const [progress, setProgress] = useState<number | null>(null);
  const player = usePlayer();

  const onFile = (file: File) => {
    setProgress(0);
    upload.mutate(
      { file, onProgress: setProgress },
      {
        onSuccess: (m) =>
          update.mutate(
            { mediaFileId: m.id },
            {
              onSuccess: () => toast.show('Audio attached — analysing…', 'success'),
              onSettled: () => setProgress(null),
            },
          ),
        onError: (err) => {
          setProgress(null);
          toast.show(errorInfo(err).message, 'error');
        },
      },
    );
  };

  const m = media.data;
  const verdict = loudnessVerdict(m?.loudnessLufs);

  return (
    <section aria-labelledby="audio-heading" className="space-y-3 rounded-xl border border-border p-5">
      <h2 id="audio-heading" className="text-lg font-semibold">
        Audio
      </h2>
      {episode.mediaFileId && media.isLoading && <Loading />}
      {m && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate font-medium" title={m.originalName ?? undefined}>
              {m.originalName ?? 'Audio file'}
            </span>
            <span className="text-muted">{formatDuration(m.durationMs)}</span>
          </div>
          {m.waveformJson?.peaks?.length ? (
            <Waveform peaks={m.waveformJson.peaks} />
          ) : isAnalysing(m, qc.getQueryState(['media', m.id])?.dataUpdateCount ?? 0, qc.getQueryData<number>(['media-analysis', m.id])) ? (
            <p className="text-xs text-muted">Analysing audio…</p>
          ) : (
            <Reanalyse mediaId={m.id} />
          )}
          <audio ref={player.ref} controls preload="metadata" src={mediaUrl(m.storagePath)} className="w-full" />
          {m.deliveryPath ? (
            <p className="text-xs text-success">
              ✓ Podcast apps get a mastered MP3{m.deliveryLufs !== null ? ` at ${m.deliveryLufs.toFixed(1)} LUFS` : ''}.
            </p>
          ) : isMastering(m, qc.getQueryData<number>(['media-analysis', m.id])) ? (
            <p className="text-xs text-muted">Preparing the mastered MP3 for podcast apps…</p>
          ) : (
            <Reanalyse
              mediaId={m.id}
              message="This file has no mastered MP3 yet, so podcast apps would get the original."
              action="Master for podcast apps"
            />
          )}
          {verdict && (
            <div>
              <Badge tone={verdict.ok ? 'success' : 'warning'}>{verdict.label}</Badge>
              {!verdict.ok && (
                <p className="mt-1 text-xs text-muted">That’s the original recording; the MP3 podcast apps get is mastered to −16 LUFS.</p>
              )}
            </div>
          )}
        </div>
      )}
      <AudioDrop onFile={onFile} progress={progress} label={episode.mediaFileId ? 'Replace audio' : 'Choose audio file'} />
    </section>
  );
}

function Reanalyse({
  mediaId,
  message = 'Analysis didn’t finish, so length and loudness are unknown.',
  action = 'Re-analyse',
}: {
  mediaId: string;
  message?: string;
  action?: string;
}) {
  const analyze = useAnalyzeMedia(mediaId);
  const toast = useToast();
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-warning-soft p-3 text-xs text-warning">
      <span>{message}</span>
      <Button
        size="sm"
        variant="secondary"
        loading={analyze.isPending}
        onClick={() =>
          analyze.mutate(undefined, {
            onSuccess: () => toast.show('Re-analysing audio…', 'info'),
            onError: (err) => toast.show(errorInfo(err).message, 'error'),
          })
        }
      >
        {action}
      </Button>
    </div>
  );
}

/** Public URL for a stored object (served by the API's /media route). */
export function mediaUrl(storagePath: string): string {
  return `/media/${storagePath.split('/').map(encodeURIComponent).join('/')}`;
}

function PublishCard({ episode }: { episode: ShowEpisode }) {
  const update = useUpdateEpisode(episode);
  const refresh = useRefreshFeed(episode.podcastId);
  const toast = useToast();
  const published = episode.status === 'published';
  const checks = [
    { label: 'Audio attached', done: !!episode.mediaFileId },
    { label: 'Title', done: !!episode.title.trim() },
    { label: 'Summary', done: !!episode.description?.trim() },
  ];

  const setStatus = (status: 'published' | 'draft') =>
    update.mutate(
      { status },
      {
        onSuccess: () => {
          refresh.mutate();
          toast.show(status === 'published' ? 'Published — the feed updates within a minute' : 'Moved back to draft', 'success');
        },
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );

  return (
    <section aria-labelledby="publish-heading" className="space-y-3 rounded-xl border border-border p-5">
      <h2 id="publish-heading" className="text-lg font-semibold">
        Publish
      </h2>
      {published ? (
        <p className="text-sm text-muted">
          Live in your feed since {episode.publishedAt ? new Date(episode.publishedAt).toLocaleString() : 'now'}.
        </p>
      ) : (
        <ul className="space-y-1 text-sm">
          {checks.map((c) => (
            <li key={c.label} className={c.done ? 'text-text' : 'text-muted'}>
              <span aria-hidden className={c.done ? 'text-success' : ''}>
                {c.done ? '✓ ' : '○ '}
              </span>
              {c.label}
            </li>
          ))}
        </ul>
      )}
      {published ? (
        <Button variant="secondary" loading={update.isPending} onClick={() => setStatus('draft')}>
          Unpublish
        </Button>
      ) : (
        <Button loading={update.isPending} disabled={!episode.mediaFileId} onClick={() => setStatus('published')}>
          Publish now
        </Button>
      )}
    </section>
  );
}
